import Actions from 'flow/actions';
import { companionCommand, isConnected } from 'flow/api-clients/top-client';
import {
    generateCompanionNonce,
    isCompanionSignallingSurface,
    isDesignatedCompanion,
} from 'flow/companion/companion-utils';
import { getCompanionDeviceId } from 'flow/companion/companion-device-id';
import { selectDesignatedCompanionDeviceId } from 'flow/selectors/app-selectors';
import { selectCompanionResponsive } from 'flow/selectors/companion-selectors';
import { State } from 'flow/types/State';
import { AppDispatch } from 'flow/types/Redux';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';

export const COMPANION_PING_INTERVAL_MS = 15000;
export const COMPANION_PONG_TIMEOUT_MS = 5000;
export const COMPANION_MAX_MISSED_PINGS = 2;

type HeartbeatMessage = {
    deviceId?: string;
    nonce?: string;
};

let missedPings = 0;
let awaitingPong = false;
let timer: ReturnType<typeof setTimeout> | null = null;

const ignoreFailure = () => {};

const describeResponsive = (responsive: boolean | null) => {
    if (responsive === null) {
        return 'Companion heartbeat stopped';
    }
    return responsive
        ? 'Companion is responsive'
        : 'Companion is not responding';
};

const setCompanionResponsive =
    (responsive: boolean | null) =>
    (dispatch: AppDispatch, getState: () => State) => {
        if (selectCompanionResponsive(getState()) === responsive) {
            return;
        }
        addUserPhoneLogEntry(describeResponsive(responsive));
        dispatch({ type: Actions.APP_COMPANION_RESPONSIVE, responsive });
    };

const clearTimer = () => {
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
};

export const resetCompanionHeartbeat = () => (dispatch: AppDispatch) => {
    clearTimer();
    missedPings = 0;
    awaitingPong = false;
    dispatch(setCompanionResponsive(null));
};

const sendPing = () => (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const deviceId = selectDesignatedCompanionDeviceId(state);

    if (!isCompanionSignallingSurface(state) || !deviceId) {
        dispatch(resetCompanionHeartbeat());
        return;
    }

    awaitingPong = true;
    if (isConnected()) {
        companionCommand({
            command: 'ping',
            nonce: generateCompanionNonce(),
            deviceId,
        }).catch(ignoreFailure);
    }

    timer = setTimeout(() => dispatch(checkPong()), COMPANION_PONG_TIMEOUT_MS);
};

const checkPong = () => (dispatch: AppDispatch) => {
    const nextPing = () => {
        timer = setTimeout(
            () => dispatch(sendPing()),
            COMPANION_PING_INTERVAL_MS - COMPANION_PONG_TIMEOUT_MS,
        );
    };

    if (!awaitingPong) {
        nextPing();
        return;
    }

    missedPings += 1;
    if (missedPings < COMPANION_MAX_MISSED_PINGS) {
        dispatch(sendPing());
        return;
    }

    dispatch(setCompanionResponsive(false));
    nextPing();
};

export const startCompanionHeartbeat = () => (dispatch: AppDispatch) => {
    dispatch(resetCompanionHeartbeat());
    dispatch(sendPing());
    return () => {
        dispatch(resetCompanionHeartbeat());
    };
};

export const handleCompanionPong =
    (data: HeartbeatMessage) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const deviceId = selectDesignatedCompanionDeviceId(getState());
        if (!deviceId || data?.deviceId !== deviceId) {
            return;
        }
        awaitingPong = false;
        missedPings = 0;
        dispatch(setCompanionResponsive(true));
    };

export const answerCompanionPing =
    (data: HeartbeatMessage) =>
    (_dispatch: AppDispatch, getState: () => State) => {
        const deviceId = getCompanionDeviceId();
        if (
            !isDesignatedCompanion(getState()) ||
            !deviceId ||
            data?.deviceId !== deviceId ||
            !data?.nonce ||
            !isConnected()
        ) {
            return;
        }

        companionCommand({
            command: 'pong',
            nonce: data.nonce,
            deviceId,
        }).catch(ignoreFailure);
    };
